// Marketing attribution captured off the landing-page URL: Google click ids
// (gclid/gbraid/wbraid — which one appears depends on device/click path, so we
// capture all three) plus the five standard UTM tags. Stored at page load and
// read back when the session is minted, because the params only exist on the
// URL at landing and are lost on the first in-site navigation.
export const ATTRIBUTION_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
export type Attribution = Partial<Record<AttributionKey, string>>;

// sessionStorage (not local): attribution belongs to the browsing session that
// began at the ad click and should not leak into a later organic visit in a
// fresh tab. Page-level, not variant-scoped — one URL feeds any embeds on the
// page.
const STORAGE_KEY = "public-jan-attribution";

// Cap stored value length so a junk/hostile URL can't bloat storage. The
// backend enforces its own cap when persisting.
const MAX_VALUE_LEN = 512;

/**
 * Read attribution params off the current URL and store them in
 * sessionStorage. Last-click-wins: when any attribution param is present the
 * stored blob is replaced wholesale — the latest click owns the conversion,
 * and merging would let stale fields from an earlier differently-tagged click
 * survive (e.g. an old gclid riding along with new UTM params). Safe to call
 * on every page load; no-op when no attribution params are present.
 */
export const captureAttribution = (): void => {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }

  const incoming: Attribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key);
    if (value) incoming[key] = value.slice(0, MAX_VALUE_LEN);
  }
  if (Object.keys(incoming).length === 0) return;

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(incoming));
  } catch {
    // Storage disabled / quota exceeded — attribution is best-effort.
  }
};

/**
 * Read stored attribution back. Returns {} when nothing was captured or the
 * stored blob is unreadable. Filters to known keys with string values so a
 * tampered blob can't inject arbitrary fields into the session-start body.
 */
export const getAttribution = (): Attribution => {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return {};
  }
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const clean: Attribution = {};
    for (const key of ATTRIBUTION_KEYS) {
      const v = parsed[key];
      // Re-cap on read: sessionStorage is shared with every script on the
      // host origin, so an oversize value written by someone else must not
      // reach the network request.
      if (typeof v === "string" && v) clean[key] = v.slice(0, MAX_VALUE_LEN);
    }
    return clean;
  } catch {
    return {};
  }
};
